/* eslint-disable no-nested-ternary */
/* eslint-disable prettier/prettier */
import React, { useEffect, useRef, useState } from 'react';

import axios, { CancelTokenSource } from 'axios';
import imageCompression from 'browser-image-compression';
import { useRouter } from 'next/dist/client/router';
import { useDropzone } from 'react-dropzone';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useAccount } from 'wagmi';

import editFile from '@/api/editFile';
import { getFileAPI } from '@/api/getFile';
import { logAnalyticsAPI } from '@/api/logAnalytics';
import Wrapper from '@/components/BodyLayout/Wrapper';
import Page500 from '@/components/ErrorPage/Page500';
import FileEditScreen from '@/components/PreviewScreen/FileEditScreen';
import FilePreview from '@/components/PreviewScreen/FileScreen';
import FileSideTab from '@/components/PreviewScreen/FileSideTab';
import FinalUpload from '@/components/Shareaway/FinalUpload';
import Shareaway from '@/components/Shareaway/Shareaway';
import { Spinner } from '@/components/Spinner/Spinner';
import UserRestrictedScreen from '@/components/UserRestrictedScreen/UserRestrictedScreen';
import useMediaQuery from '@/hooks/useMediaQuery';
import errorMessages from '@/utils/errorMessages.json';
import { sendNotifcation } from '@/utils/notification';
import { GetFileResponse } from 'interfaces/ApiType/apiType';

const Files = () => {
  const router = useRouter();
  const fileId = router.query.fileId as string;
  const editAudience = router.query.editAudience === 'true';
  const ownerFirstVisit = router.query.ownerFirstVisit as string;
  const [fileResponse, setFileResponse] = useState<GetFileResponse>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [errorPage, setErrorPage] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isComponentVisible, setIsComponentVisible] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const isMediaMax1025px = useMediaQuery('(max-width : 1025px)');
  const hiddenFileInput = useRef(null);
  const [cancelTokenSource, setCancelTokenSource] =
    useState<CancelTokenSource | null>(null);
  const [fileName, setFileName] = useState('');
  const {data: signer} = useAccount()
  const [isEditClicked, setIsEditClicked] = useState(false);
  const [isLexical, setIsLexical] = useState(false);
  const tabQueryParameter = router.query.tab as string;

  const getFile = async (id: string) => {
    if (!id) return;
    setIsLoading(true);
    try {
      const file = await getFileAPI({ fileId: id });
      if (file.status === 404) {
        sendNotifcation('File does not exists', '', 'danger');
        router.push('/404');
      }

      if (file) {
        setFileResponse(file)
      }
      if (file.status === 401 || file.status === 403){
        setIsComponentVisible(true);
      }
      if(file.status === 500){
        setErrorPage(true);
      }
    } catch (err) {
      console.log(err);
    }
    setIsLoading(false);
  };
  const handleDrop = async (acceptedFiles: File[]) => {
    let files = acceptedFiles[0];

    const options = {
      maxSizeMB: 1000,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
    };
    if (files?.type.includes('image')) {
      files = (await imageCompression(files, options)) as any;
    }

    setIsUploading(true);
    const source = axios.CancelToken.source();
    setCancelTokenSource(source);

    if (files.size > 20971520) {
      cancelTokenSource?.cancel();
      setIsUploading(false);
      sendNotifcation('Maximum allowed file size is 20MB', '', 'danger');
      return;
    }

    try {
      const onUploadProgress = (progressEvent: any) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded * 100) / progressEvent.total
        );
        setProgress(percentCompleted);
      };
      const recaptchaToken = await executeRecaptcha('file');
      const obj = {
        files,
        recaptchaToken,
        fileId,
        config: {
          onUploadProgress,
          cancelToken: source.token,
        },
      };
      setFileName(files?.name);
      const response = await editFile(obj);
      if (response.status === 200) {
        getFile(fileId);
        setIsEditClicked(false);
        setIsUploading(false);
      }
    } catch (error: any) {
      setIsUploading(false);
      if(error.response?.status >= 500){
        setErrorPage(true);
        return;
      }
      if(error.response?.status === 403){
        sendNotifcation(errorMessages[403], '', 'danger');
        return;
      }
      if(error.response?.status === 401){
        sendNotifcation(errorMessages[401], '', 'danger');
      }
      sendNotifcation('Unable to edit file', '', 'danger');
    }
  };
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    maxFiles: 1,
    noClick: true,
  });

  const logAnalytics = async () => {
    try {
      await logAnalyticsAPI('view', fileId);
    } catch (err) {
      console.log(err)
    }
  };
  const dynamicRoute = useRouter().asPath;

  useEffect(() => {
    getFile(fileId);
    logAnalytics();
  }, [fileId, isComponentVisible, dynamicRoute, signer?.address]);

  if(errorPage){
    return (
      <Wrapper>
        <Page500/>
      </Wrapper>
    )
  }

  if (isComponentVisible) {
    return (
      <UserRestrictedScreen
        contractAddress={fileResponse.data.token.contractAddress}
        tokenName={fileResponse.data.token.name}
        chain={fileResponse.data.token.chain}
        gateBalance={fileResponse.data.token.gateBalance}
        explorerLink={fileResponse.data.token.explorerLink}
      />
    );
  }
  if (isLoading) {
    return (
      <Wrapper>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-fit w-fit">
            <span className="font-semibold text-gray-500">
              <Spinner />
            </span>
          </div>
        </div>
      </Wrapper>
    );
  }
  if ((editAudience && fileResponse.data.currentPermission.edit) || (router.query.setAccess && fileResponse.data.currentPermission.edit ))
    return <Shareaway fileResponse={fileResponse} setErrorPage={setErrorPage}/>;
  if (editAudience && ownerFirstVisit === 'true' && !fileResponse.data.owner)
    return <FinalUpload fileResponse={fileResponse} />;

  return (
    <>
      {fileResponse && fileResponse.data.mimetype && (
        <Wrapper>
          {errorPage? (
            <Page500/>
          ): (
            isMediaMax1025px ? (
              <div className="w-full h-full">
                <FileSideTab
                  file={fileResponse}
                  ownerFirstVisit={ownerFirstVisit === 'true'}
                  isActive={'File'}
                  handleFileEdit={handleDrop}
                  setIsLoginLoading={setIsLoading}
                  setIsEditClicked={setIsEditClicked}
                  isEditClicked={isEditClicked}
                  setIsLexical={setIsLexical}
                  isLexical={isLexical}
                  getFile={getFile}
                  tabQueryParameter={tabQueryParameter}
                  setErrorPage={setErrorPage}
                />
              </div>
            ) : (
              <div className="w-[100%] justify-center flex">
                <div className="w-[100%] grid grid-cols-4  h-full max-w-[1800px]">
                  <div className=" col-span-3 mr-4">
                    {isEditClicked && !isLexical ? (
                      <FileEditScreen
                        setIsEditClicked={setIsEditClicked}
                        handleDrop={handleDrop}
                        progress={progress}
                        cancelTokenSource={cancelTokenSource}
                        fileName={fileName}
                        getRootProps={getRootProps}
                        getInputProps={getInputProps}
                        isDragActive={isDragActive}
                        hiddenFileInput={hiddenFileInput}
                        isUploading={isUploading}
                        setIsUploading={setIsUploading}
                      />
                    ) : (
                      <FilePreview
                        setIsEditClicked={setIsEditClicked}
                        isEditClicked={isEditClicked}
                        fileResponse={fileResponse}
                        setIsLexical={setIsLexical}
                      />
                    )}
                  </div>
  
                  <div className="flex justify-center">
                    <FileSideTab
                      file={fileResponse}
                      ownerFirstVisit={ownerFirstVisit === 'true'}
                      isActive="About"
                      handleFileEdit={handleDrop}
                      setIsLoginLoading={setIsLoading}
                      setIsEditClicked={setIsEditClicked}
                      isEditClicked={isEditClicked}
                      setIsLexical={setIsLexical}
                      isLexical={isLexical}
                      getFile={getFile}
                      tabQueryParameter={tabQueryParameter}
                    />
                  </div>
                </div>
              </div>
            )
          )}

        </Wrapper>
      )}
    </>
  );
};

export default Files;

// check